/**
 * Toast & Confirm-Dialog system.
 *
 * Provides a `useToast()` hook with two methods:
 *
 *   const { notify, confirm } = useToast();
 *   notify('Gespeichert', 'success');                 // tiny banner, auto-fades
 *   notify('Fehler: ' + err.message, 'error');
 *   const ok = await confirm({ title: '…', message: '…' });
 *   if (ok) doIt();
 *
 * Replaces the ~25 scattered window.alert / window.confirm calls — those
 * are blocking, ugly, and don't match the rest of the dark glass UI.
 */
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { CheckCircle2, AlertCircle, Info, X } from 'lucide-react';

const ToastContext = createContext(null);

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Defensive: lets callers still work if the provider isn't mounted
    // (e.g. early bootstrap errors), just falls back to native browser UI.
    return {
      notify: (msg) => console.log('[toast:fallback]', msg),
      confirm: ({ message }) => Promise.resolve(window.confirm(message)),
    };
  }
  return ctx;
};

export function ToastProvider({ children }) {
  // Toast queue
  const [toasts, setToasts] = useState([]);
  // Single in-flight confirm dialog (rare to need >1 simultaneously)
  const [confirmState, setConfirmState] = useState(null);

  const notify = useCallback((message, type = 'info', durationMs = 3500) => {
    const id = Date.now() + Math.random();
    setToasts((arr) => [...arr, { id, message, type }]);
    if (durationMs > 0) {
      setTimeout(() => {
        setToasts((arr) => arr.filter((t) => t.id !== id));
      }, durationMs);
    }
    return id;
  }, []);

  const dismiss = useCallback((id) => {
    setToasts((arr) => arr.filter((t) => t.id !== id));
  }, []);

  const confirm = useCallback(({ title, message, confirmLabel = 'Bestätigen', cancelLabel = 'Abbrechen', variant = 'default' } = {}) => {
    return new Promise((resolve) => {
      setConfirmState({ title, message, confirmLabel, cancelLabel, variant, resolve });
    });
  }, []);

  const handleConfirmClose = useCallback((result) => {
    setConfirmState((s) => {
      if (s) s.resolve(result);
      return null;
    });
  }, []);

  // ESC closes the confirm dialog as cancel
  useEffect(() => {
    if (!confirmState) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') handleConfirmClose(false);
      if (e.key === 'Enter') handleConfirmClose(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [confirmState, handleConfirmClose]);

  return (
    <ToastContext.Provider value={{ notify, confirm, dismiss }}>
      {children}

      {/* ============ Toast Stack ============ */}
      <div
        className="fixed top-4 right-4 z-[10000] flex flex-col gap-2 pointer-events-none"
        style={{ maxWidth: 'calc(100vw - 32px)' }}
        aria-live="polite"
        aria-atomic="false"
      >
        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
        ))}
      </div>

      {/* ============ Confirm Dialog ============ */}
      {confirmState && (
        <div
          className="fixed inset-0 z-[10001] flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)' }}
          onClick={(e) => { if (e.target === e.currentTarget) handleConfirmClose(false); }}
        >
          <div
            className="rounded-2xl max-w-md w-full overflow-hidden"
            style={{ background: '#0A0A0A', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 20px 60px rgba(0,0,0,0.6)' }}
          >
            <div className="p-6">
              {confirmState.title && (
                <h3 className="font-semibold text-white text-lg mb-2">{confirmState.title}</h3>
              )}
              <p className="text-white/70 text-sm whitespace-pre-line leading-relaxed">{confirmState.message}</p>
            </div>
            <div className="flex gap-2 px-6 pb-6 justify-end">
              <button
                onClick={() => handleConfirmClose(false)}
                className="px-4 py-2 rounded-lg text-sm text-white/70 hover:text-white hover:bg-white/5 transition-colors"
              >
                {confirmState.cancelLabel}
              </button>
              <button
                onClick={() => handleConfirmClose(true)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  confirmState.variant === 'danger'
                    ? 'bg-red-500/20 text-red-300 hover:bg-red-500/30'
                    : 'bg-orange-500 text-black hover:bg-orange-400'
                }`}
                autoFocus
              >
                {confirmState.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </ToastContext.Provider>
  );
}

function ToastItem({ toast, onDismiss }) {
  // small enter/exit animation
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const r = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(r);
  }, []);

  const styles = {
    success: { Icon: CheckCircle2, tint: '#10b981', bg: 'rgba(16,185,129,0.12)' },
    error:   { Icon: AlertCircle,  tint: '#ef4444', bg: 'rgba(239,68,68,0.12)' },
    info:    { Icon: Info,         tint: '#F97316', bg: 'rgba(249,115,22,0.12)' },
  }[toast.type] || { Icon: Info, tint: '#F97316', bg: 'rgba(249,115,22,0.12)' };

  const { Icon, tint, bg } = styles;

  return (
    <div
      className="pointer-events-auto rounded-xl px-4 py-3 flex items-start gap-3 backdrop-blur-xl border min-w-[280px] max-w-md"
      style={{
        background: '#0A0A0A',
        borderColor: 'rgba(255,255,255,0.1)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateX(0)' : 'translateX(20px)',
        transition: 'opacity 200ms ease, transform 200ms ease',
      }}
      role="status"
    >
      <div className="shrink-0 mt-0.5 rounded-md p-1" style={{ background: bg }}>
        <Icon size={14} style={{ color: tint }} />
      </div>
      <p className="text-sm text-white/90 flex-1 leading-relaxed">{toast.message}</p>
      <button
        onClick={onDismiss}
        className="shrink-0 p-1 text-white/30 hover:text-white/70 transition-colors -mr-1"
        aria-label="Schließen"
      >
        <X size={14} />
      </button>
    </div>
  );
}
